import { useState, useRef, useEffect, KeyboardEvent, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import { RootState } from '../../../store/store';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import './mobileFilter.scss';

type SeasonFilter = 'all' | 'seasonless' | 'summer' | 'autumn' | 'winter' | 'spring';

interface IFilterProps {
    checkboxItemsFiller: { id: string; name: string; checked: boolean }[];
    onCheck: (id: string) => void;
    checkboxItemsFabric?: { id: string; name: string; checked: boolean }[];
    onCheckFabric?: (id: string) => void;
    currentSeason?: SeasonFilter;
    onSeasonChange?: (season: SeasonFilter) => void;
    seasonOptions?: { value: SeasonFilter; label: string }[];
    groupFilterLabel?: string;
    groupFilterOptions?: { value: string; label: string }[];
    currentGroupOption?: string;
    onGroupOptionChange?: (value: string) => void;
}

const normalizeKey = (v: unknown) => (v == null ? '' : String(v)).trim().toLowerCase();
const getFillerKey = (p: any) => {
    const ft = p?.fillerType;
    if (ft == null) return '';
    if (typeof ft === 'string') return normalizeKey(ft);
    return normalizeKey(ft?.id ?? p?.fillerTypeId ?? ft?.url ?? ft?.slug ?? p?.fillerTypeUrl ?? p?.fillerTypeSlug);
};
const getFabricKey = (p: any) => {
    const ft = p?.fabricType;
    if (ft == null) return '';
    if (typeof ft === 'string') return normalizeKey(ft);
    return normalizeKey(ft?.id ?? p?.fabricTypeId ?? ft?.url ?? ft?.slug ?? p?.fabricTypeUrl ?? p?.fabricTypeSlug);
};

const MobileFilter: React.FC<IFilterProps> = ({
    checkboxItemsFiller,
    onCheck,
    checkboxItemsFabric = [],
    onCheckFabric,
    currentSeason = 'all',
    onSeasonChange,
    seasonOptions,
    groupFilterLabel,
    groupFilterOptions,
    currentGroupOption = 'all',
    onGroupOptionChange,
}) => {
    const [isSeasonOpen, setIsSeasonOpen] = useState(false);
    const [isSilkOpen, setIsSilkOpen] = useState(false);
    const [isFabricOpen, setIsFabricOpen] = useState(false);
    const [isSizeOpen, setIsSizeOpen] = useState(false);
    const [isColorOpen, setIsColorOpen] = useState(false);
    const [isGroupOpen, setIsGroupOpen] = useState(false);
    const seasonRef = useRef<HTMLDivElement | null>(null);
    const silkRef = useRef<HTMLDivElement | null>(null);
    const fabricRef = useRef<HTMLDivElement | null>(null);
    const sizeRef = useRef<HTMLDivElement | null>(null);
    const colorRef = useRef<HTMLDivElement | null>(null);
    const groupRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { url } = useParams();
    const { products } = useSelector((s: RootState) => s.product);

    useBodyScrollLock(open);

    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (isSeasonOpen && seasonRef.current && !seasonRef.current.contains(e.target as Node)) {
                setIsSeasonOpen(false);
            }
            if (isSilkOpen && silkRef.current && !silkRef.current.contains(e.target as Node)) {
                setIsSilkOpen(false);
            }
            if (isFabricOpen && fabricRef.current && !fabricRef.current.contains(e.target as Node)) {
                setIsFabricOpen(false);
            }
            if (isGroupOpen && groupRef.current && !groupRef.current.contains(e.target as Node)) {
                setIsGroupOpen(false);
            }
            if (isSizeOpen && sizeRef.current && !sizeRef.current.contains(e.target as Node)) {
                setIsSizeOpen(false);
            }
            if (isColorOpen && colorRef.current && !colorRef.current.contains(e.target as Node)) {
                setIsColorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [isSeasonOpen, isSilkOpen, isFabricOpen, isGroupOpen, isSizeOpen, isColorOpen]);

    const handleSeasonKey = (e: KeyboardEvent<HTMLElement>, value: SeasonFilter) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSeasonChange?.(value);
            setIsSeasonOpen(false);
        }
    };

    const handleGroupKey = (e: KeyboardEvent<HTMLElement>, value: string) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onGroupOptionChange?.(value);
            setIsGroupOpen(false);
        }
    };

    const params = new URLSearchParams(location.search);
    const isDiscountsRoute = url === 'discounts';

    const sourceProducts = useMemo(() => {
        if (isDiscountsRoute) return products.filter((p: any) => p?.promoType != null);
        if (url) return products.filter((p: any) => p?.category?.url === url);
        return [...products];
    }, [products, url, isDiscountsRoute]);

    const extractPrice = (p: any): number | null => {
        const candidates = [p?.price, p?.currentPrice, p?.finalPrice, p?.discountPrice, p?.cost];
        for (const v of candidates) {
            if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
            if (typeof v === 'string' && v.trim() !== '' && !isNaN(+v)) return +v;
        }
        return null;
    };

    const selectedSize = params.get('size') || 'all';
    const selectedColor = params.get('color') || 'all';
    const urlPriceFrom = params.get('priceFrom');
    const urlPriceTo = params.get('priceTo');

    // --- FILLER: выбранные id ---
    const checkedFillerTypeIds = useMemo(() => {
        return new Set(checkboxItemsFiller.filter((c) => c.checked).map((c) => c.id));
    }, [checkboxItemsFiller]);

    // --- FABRIC: выбранные id ---
    const checkedFabricTypeIds = useMemo(() => {
        return new Set((checkboxItemsFabric ?? []).filter((c) => c.checked).map((c) => c.id));
    }, [checkboxItemsFabric]);

    // База для расчёта size/color/price: AND(Filler + Fabric) + Season
    const fillerFabricSeasonFiltered = useMemo(() => {
        const byTypes = sourceProducts.filter((p: any) => {
            const passFiller =
                checkedFillerTypeIds.size === 0
                    ? true
                    : (() => {
                          //   const pid = String(p?.fillerType?.id ?? p?.fillerTypeId ?? p?.fillerType?.url ?? '');
                          //   return pid ? checkedFillerTypeIds.has(pid) : false;
                          const pid = getFillerKey(p);
                          return pid ? checkedFillerTypeIds.has(pid) : false;
                      })();

            const passFabric =
                checkedFabricTypeIds.size === 0
                    ? true
                    : (() => {
                          //   const pid = String(p?.fabricType?.id ?? p?.fabricTypeId ?? p?.fabricType?.url ?? '');
                          //   return pid ? checkedFabricTypeIds.has(pid) : false;
                          const pid = getFabricKey(p);
                          return pid ? checkedFabricTypeIds.has(pid) : false;
                      })();

            return passFiller && passFabric;
        });

        if (currentSeason === 'all') return byTypes;
        if (currentSeason === 'seasonless') return byTypes.filter((p: any) => p?.season == null);
        return byTypes.filter((p: any) => String(p?.season ?? '').toLowerCase() === currentSeason);
    }, [sourceProducts, checkedFillerTypeIds, checkedFabricTypeIds, currentSeason]);

    const sizeOptions = useMemo(() => {
        const set = new Set<string>();
        fillerFabricSeasonFiltered.forEach((p: any) => {
            if (p?.group?.url === 'Размер' && p?.group?.optionValue) {
                const v = String(p.group.optionValue).trim();
                if (v) set.add(v);
            }
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [fillerFabricSeasonFiltered]);

    const colorOptions = useMemo(() => {
        const set = new Set<string>();
        fillerFabricSeasonFiltered.forEach((p: any) => {
            if (p?.group?.url === 'Цвет' && p?.group?.optionValue) {
                const v = String(p.group.optionValue).trim();
                if (v) set.add(v);
            }
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [fillerFabricSeasonFiltered]);

    const fullyFilteredForPrice = useMemo(() => {
        return fillerFabricSeasonFiltered.filter((p: any) => {
            const passSize = selectedSize === 'all' ? true : p?.group?.url === 'Размер' && String(p?.group?.optionValue ?? '') === selectedSize;
            const passColor = selectedColor === 'all' ? true : p?.group?.url === 'Цвет' && String(p?.group?.optionValue ?? '') === selectedColor;
            return passSize && passColor;
        });
    }, [fillerFabricSeasonFiltered, selectedSize, selectedColor]);

    const [priceMinBound, priceMaxBound] = useMemo(() => {
        const nums = fullyFilteredForPrice.map(extractPrice).filter((n): n is number => n != null);
        if (nums.length === 0) return [0, 0];
        return [Math.min(...nums), Math.max(...nums)];
    }, [fullyFilteredForPrice]);

    const [priceFrom, setPriceFrom] = useState<number>(priceMinBound);
    const [priceTo, setPriceTo] = useState<number>(priceMaxBound);

    useEffect(() => {
        const initFrom = urlPriceFrom ? Math.max(priceMinBound, Math.min(+urlPriceFrom, priceMaxBound)) : priceMinBound;
        const initTo = urlPriceTo ? Math.max(priceMinBound, Math.min(+urlPriceTo, priceMaxBound)) : priceMaxBound;
        if (initFrom !== priceFrom) setPriceFrom(initFrom);
        if (initTo !== priceTo) setPriceTo(initTo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceMinBound, priceMaxBound, urlPriceFrom, urlPriceTo]);

    const handlePriceFrom = (v: number) => {
        setPriceFrom(Math.min(v, priceTo));
    };
    const handlePriceTo = (v: number) => {
        setPriceTo(Math.max(v, priceFrom));
    };

    const routeKey = useMemo<'all' | 'pillows' | 'blankets' | 'accessories'>(() => {
        if (!url || url === 'discounts') return 'all';
        if (url === 'pillows') return 'pillows';
        if (url === 'blankets') return 'blankets';
        if (url === 'accessories') return 'accessories';
        return 'all';
    }, [url]);

    const showSeason = useMemo(() => {
        const haveSeasonOptions = !!seasonOptions && seasonOptions.length > 0 && !!onSeasonChange;
        if (!haveSeasonOptions) return false;
        if (routeKey === 'pillows') return false;
        if (routeKey === 'accessories') return false;
        return true;
    }, [routeKey, seasonOptions, onSeasonChange]);

    const showSize = useMemo(() => {
        if (routeKey === 'accessories') return false;
        return sizeOptions.length > 0;
    }, [routeKey, sizeOptions.length]);

    const showColor = useMemo(() => {
        if (routeKey === 'pillows' || routeKey === 'blankets') return false;
        return colorOptions.length > 0;
    }, [routeKey, colorOptions.length]);

    // const showFiller = checkboxItemsFiller.length > 0;
    // const showFabric = (checkboxItemsFabric ?? []).length > 0;
    const showFiller = useMemo(() => sourceProducts.some((p: any) => !!getFillerKey(p)), [sourceProducts]);
    const showFabric = useMemo(() => sourceProducts.some((p: any) => !!getFabricKey(p)), [sourceProducts]);
    const showPrice = priceMaxBound > priceMinBound;

    const fillerChecked = checkboxItemsFiller.filter((c) => c.checked);
    const fillerLabel = fillerChecked.length === 0 ? 'Выбрать' : fillerChecked.length === 1 ? fillerChecked[0].name : `${fillerChecked.length} вида`;

    const fabricChecked = (checkboxItemsFabric ?? []).filter((c) => c.checked);
    const fabricLabel = fabricChecked.length === 0 ? 'Выбрать' : fabricChecked.length === 1 ? fabricChecked[0].name : `${fabricChecked.length} вида`;

    const sizeSelectedLabel = selectedSize === 'all' ? 'Все' : selectedSize;
    const colorSelectedLabel = selectedColor === 'all' ? 'Все' : selectedColor;

    const basePath = url ? `/catalog/${url}` : location.pathname;
    const pushParams = (mutator: (p: URLSearchParams) => void) => {
        const p = new URLSearchParams(location.search);
        mutator(p);
        const search = p.toString();
        navigate(`${basePath}${search ? `?${search}` : ''}`, { replace: false });
    };

    const applyPriceToUrl = () => {
        pushParams((p) => {
            if (showPrice) {
                p.set('priceFrom', String(priceFrom));
                p.set('priceTo', String(priceTo));
            } else {
                p.delete('priceFrom');
                p.delete('priceTo');
            }
        });
    };

    const resetMobileSpecific = () => {
        setPriceFrom(priceMinBound);
        setPriceTo(priceMaxBound);
        pushParams((p) => {
            p.delete('priceFrom');
            p.delete('priceTo');
            p.delete('size');
            p.delete('color');
        });
    };

    const selectSize = (value: string) => {
        pushParams((p) => {
            if (value === 'all') p.delete('size');
            else p.set('size', value);
        });
        setIsSizeOpen(false);
    };

    const selectColor = (value: string) => {
        pushParams((p) => {
            if (value === 'all') p.delete('color');
            else p.set('color', value);
        });
        setIsColorOpen(false);
    };

    const resetAllFilters = (e?: any) => {
        e?.stopPropagation?.();
        setIsSeasonOpen(false);
        setIsSilkOpen(false);
        setIsFabricOpen(false);
        setIsSizeOpen(false);
        setIsColorOpen(false);
        setIsGroupOpen(false);
        setPriceFrom(priceMinBound);
        setPriceTo(priceMaxBound);
        pushParams((p) => {
            p.delete('typesOfFiller');
            p.delete('typesOfFabric');
            p.delete('season');
            p.delete('groupOption');
            p.delete('size');
            p.delete('color');
            p.delete('priceFrom');
            p.delete('priceTo');
        });
    };

    const hasAnyFilter = useMemo(() => {
        const p = new URLSearchParams(location.search);
        const hasTypesOfFiller = !!p.get('typesOfFiller');
        const hasTypesOfFabric = !!p.get('typesOfFabric');
        const hasSeason = !!p.get('season');
        const hasGroup = !!p.get('groupOption');
        const hasSize = !!p.get('size');
        const hasColor = !!p.get('color');
        const hasPrice = !!p.get('priceFrom') || !!p.get('priceTo');
        return hasTypesOfFiller || hasTypesOfFabric || hasSeason || hasGroup || hasSize || hasColor || hasPrice;
    }, [location.search]);

    return (
        <div className="mobileFilter">
            <ActionLinkButton text="Фильтры" iconSrc="/icons/new/filter.svg" iconPosition="end" onClick={() => setOpen(true)} className="mobileFilter__button" />

            <div className={`${open ? 'mobileFilter__overlay mobileFilter__overlay_open' : 'mobileFilter__overlay'}`} onClick={() => setOpen(false)} />
            <div className={`${open ? 'mobileFilter__box mobileFilter__box_open' : 'mobileFilter__box'}`}>
                <div className="mobileFilter__header" onClick={() => setOpen(false)}>
                    <img className="mobileFilter__header_logo" src="/logo.png" alt="С. Габай" />
                    <span className="mobileFilter__header_title">Фильтры</span>
                    <ActionLinkButton
                        ariaLabel="Закрыть"
                        iconSrc="/icons/mobile/close.svg"
                        iconSrcDark="/icons/mobile/close_w.svg"
                        className="mobileFilter__header_close"
                        onClick={() => setOpen(false)}
                    />
                </div>

                <div className="mobileFilter__items">
                    {/* Цена */}
                    {showPrice && (
                        <div className="mobileFilter__items_section">
                            <div className="mobileFilter__items_section_title">Цена</div>
                            <div className="mobileFilter__items_section_range">
                                <div className="mobileFilter__items_section_range_inputs">
                                    <input
                                        aria-label="Минимальная цена"
                                        type="range"
                                        min={priceMinBound}
                                        max={priceMaxBound}
                                        step={1}
                                        value={priceFrom}
                                        onChange={(e) => handlePriceFrom(+e.target.value)}
                                        className="mobileFilter__items_section_range_inputs_elem"
                                    />
                                    <input
                                        aria-label="Максимальная цена"
                                        type="range"
                                        min={priceMinBound}
                                        max={priceMaxBound}
                                        step={1}
                                        value={priceTo}
                                        onChange={(e) => handlePriceTo(+e.target.value)}
                                        className="mobileFilter__items_section_range_inputs_elem"
                                    />
                                    <div
                                        className="mobileFilter__items_section_range_inputs_rangeTrack"
                                        style={{
                                            ['--min' as any]: `${priceMinBound}`,
                                            ['--max' as any]: `${priceMaxBound}`,
                                            ['--from' as any]: `${priceFrom}`,
                                            ['--to' as any]: `${priceTo}`,
                                        }}
                                    />
                                </div>
                                <div className="mobileFilter__items_section_range_values">
                                    <div className="mobileFilter__items_section_range_values_chip">от {Math.round(priceFrom)}</div>
                                    <div className="mobileFilter__items_section_range_values_chip">до {Math.round(priceTo)}</div>
                                </div>
                                <div className="mobileFilter__items_section_range_row">
                                    <button
                                        className="mobileFilter__items_section_range_row_elem mobileFilter__items_section_range_row_elem_reset"
                                        onClick={resetMobileSpecific}
                                    >
                                        Сбросить
                                    </button>
                                    <button
                                        className="mobileFilter__items_section_range_row_elem mobileFilter__items_section_range_row_elem_apply"
                                        onClick={applyPriceToUrl}
                                    >
                                        Применить
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Тип ткани */}
                    {showFabric && (
                        <div className="mobileFilter__items_section" ref={fabricRef}>
                            <button
                                className="mobileFilter__items_section_dropdownToggle"
                                aria-haspopup="true"
                                aria-expanded={isFabricOpen}
                                onClick={() => setIsFabricOpen((v) => !v)}
                            >
                                <span className="mobileFilter__items_section_dropdownToggle_label">Тип ткани:</span>
                                <span className="mobileFilter__items_section_dropdownToggle_selected">{fabricLabel}</span>
                                <span
                                    className={`mobileFilter__items_section_dropdownToggle_arrow ${
                                        isFabricOpen ? 'mobileFilter__items_section_dropdownToggle_arrow_open' : ''
                                    }`}
                                    aria-hidden="true"
                                >
                                    ▼
                                </span>
                            </button>
                            <div
                                className={`mobileFilter__items_section_dropdownPanel mobileFilter__items_section_dropdownPanel_multi ${
                                    isFabricOpen ? 'mobileFilter__items_section_dropdownPanel_open' : ''
                                }`}
                                role="group"
                                aria-label="Тип ткани"
                            >
                                <div className="mobileFilter__items_section_dropdownPanel_list">
                                    {(checkboxItemsFabric ?? []).map((item, index) => (
                                        <label key={`${index}-${item.id}`} className="mobileFilter__items_section_dropdownPanel_list_elem">
                                            <input
                                                type="checkbox"
                                                checked={item.checked}
                                                onChange={() => onCheckFabric?.(item.id)}
                                                className="mobileFilter__items_section_dropdownPanel_list_elem_checkbox"
                                            />
                                            <span className="mobileFilter__items_section_dropdownPanel_list_elem_checkbox_text">{item.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Тип наполнителя */}
                    {showFiller && (
                        <div className="mobileFilter__items_section" ref={silkRef}>
                            <button
                                className="mobileFilter__items_section_dropdownToggle"
                                aria-haspopup="true"
                                aria-expanded={isSilkOpen}
                                onClick={() => setIsSilkOpen((v) => !v)}
                            >
                                <span className="mobileFilter__items_section_dropdownToggle_label">Наполнитель:</span>
                                <span className="mobileFilter__items_section_dropdownToggle_selected">{fillerLabel}</span>
                                <span
                                    className={`mobileFilter__items_section_dropdownToggle_arrow ${
                                        isSilkOpen ? 'mobileFilter__items_section_dropdownToggle_arrow_open' : ''
                                    }`}
                                    aria-hidden="true"
                                >
                                    ▼
                                </span>
                            </button>
                            <div
                                className={`mobileFilter__items_section_dropdownPanel mobileFilter__items_section_dropdownPanel_multi ${
                                    isSilkOpen ? 'mobileFilter__items_section_dropdownPanel_open' : ''
                                }`}
                                role="group"
                                aria-label="Тип наполнителя"
                            >
                                <div className="mobileFilter__items_section_dropdownPanel_list">
                                    {checkboxItemsFiller.map((item, index) => (
                                        <label key={`${index}-${item.id}`} className="mobileFilter__items_section_dropdownPanel_list_elem">
                                            <input
                                                type="checkbox"
                                                checked={item.checked}
                                                onChange={() => onCheck(item.id)}
                                                className="mobileFilter__items_section_dropdownPanel_list_elem_checkbox"
                                            />
                                            <span className="mobileFilter__items_section_dropdownPanel_list_elem_checkbox_text">{item.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Сезонность */}
                    {showSeason && seasonOptions && seasonOptions.length > 0 && onSeasonChange && (
                        <div className="mobileFilter__items_section" ref={seasonRef}>
                            <button
                                className="mobileFilter__items_section_dropdownToggle"
                                aria-haspopup="listbox"
                                aria-expanded={isSeasonOpen}
                                onClick={() => setIsSeasonOpen((v) => !v)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setIsSeasonOpen(false);
                                }}
                            >
                                <span className="mobileFilter__items_section_dropdownToggle_label">Сезон:</span>
                                <span className="mobileFilter__items_section_dropdownToggle_selected">
                                    {seasonOptions.find((o) => o.value === currentSeason)?.label || 'Выбрать'}
                                </span>
                                <span
                                    className={`mobileFilter__items_section_dropdownToggle_arrow ${
                                        isSeasonOpen ? 'mobileFilter__items_section_dropdownToggle_arrow_open' : ''
                                    }`}
                                    aria-hidden="true"
                                >
                                    ▼
                                </span>
                            </button>
                            <div
                                className={`mobileFilter__items_section_dropdownPanel ${isSeasonOpen ? 'mobileFilter__items_section_dropdownPanel_open' : ''}`}
                                role="listbox"
                                aria-label="Сезон"
                            >
                                {seasonOptions.map((opt, idx) => (
                                    <div
                                        key={`${opt.value}-${idx}`}
                                        role="option"
                                        aria-selected={currentSeason === opt.value}
                                        tabIndex={0}
                                        className={[
                                            'mobileFilter__items_section_dropdownPanel_dropdownItem',
                                            currentSeason === opt.value ? 'mobileFilter__items_section_dropdownPanel_dropdownItem_active' : undefined,
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => {
                                            onSeasonChange(opt.value);
                                            setIsSeasonOpen(false);
                                        }}
                                        onKeyDown={(e) => handleSeasonKey(e, opt.value)}
                                    >
                                        <span className="mobileFilter__items_section_dropdownPanel_itemLabel">{opt.label}</span>
                                        {currentSeason === opt.value && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Размер */}
                    {showSize && (
                        <div className="mobileFilter__items_section" ref={sizeRef}>
                            <button
                                className="mobileFilter__items_section_dropdownToggle"
                                aria-haspopup="listbox"
                                aria-expanded={isSizeOpen}
                                onClick={() => setIsSizeOpen((v) => !v)}
                            >
                                <span className="mobileFilter__items_section_dropdownToggle_label">Размер:</span>
                                <span className="mobileFilter__items_section_dropdownToggle_selected">{sizeSelectedLabel}</span>
                                <span
                                    className={`mobileFilter__items_section_dropdownToggle_arrow ${
                                        isSizeOpen ? 'mobileFilter__items_section_dropdownToggle_arrow_open' : ''
                                    }`}
                                    aria-hidden="true"
                                >
                                    ▼
                                </span>
                            </button>
                            <div
                                className={`mobileFilter__items_section_dropdownPanel ${isSizeOpen ? 'mobileFilter__items_section_dropdownPanel_open' : ''}`}
                                role="listbox"
                                aria-label="Размер"
                            >
                                <div
                                    role="option"
                                    aria-selected={selectedSize === 'all'}
                                    tabIndex={0}
                                    className={['mobileFilter__items_section_dropdownPanel_dropdownItem', selectedSize === 'all' ? 'active' : undefined]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => selectSize('all')}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') selectSize('all');
                                    }}
                                >
                                    <span className="mobileFilter__items_section_dropdownPanel_itemLabel">Все</span>
                                    {selectedSize === 'all' && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                </div>
                                {sizeOptions.map((opt) => (
                                    <div
                                        key={opt}
                                        role="option"
                                        aria-selected={selectedSize === opt}
                                        tabIndex={0}
                                        className={['mobileFilter__items_section_dropdownPanel_dropdownItem', selectedSize === opt ? 'active' : undefined]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => selectSize(opt)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') selectSize(opt);
                                        }}
                                    >
                                        <span className="mobileFilter__items_section_dropdownPanel_itemLabel">{opt}</span>
                                        {selectedSize === opt && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Цвет */}
                    {showColor && (
                        <div className="mobileFilter__items_section" ref={colorRef}>
                            <button
                                className="mobileFilter__items_section_dropdownToggle"
                                aria-haspopup="listbox"
                                aria-expanded={isColorOpen}
                                onClick={() => setIsColorOpen((v) => !v)}
                            >
                                <span className="mobileFilter__items_section_dropdownToggle_label">Цвет:</span>
                                <span className="mobileFilter__items_section_dropdownToggle_selected">{colorSelectedLabel}</span>
                                <span
                                    className={`mobileFilter__items_section_dropdownToggle_arrow ${
                                        isColorOpen ? 'mobileFilter__items_section_dropdownToggle_arrow_open' : ''
                                    }`}
                                    aria-hidden="true"
                                >
                                    ▼
                                </span>
                            </button>
                            <div
                                className={`mobileFilter__items_section_dropdownPanel ${isColorOpen ? 'mobileFilter__items_section_dropdownPanel_open' : ''}`}
                                role="listbox"
                                aria-label="Цвет"
                            >
                                <div
                                    role="option"
                                    aria-selected={selectedColor === 'all'}
                                    tabIndex={0}
                                    className={['mobileFilter__items_section_dropdownPanel_dropdownItem', selectedColor === 'all' ? 'active' : undefined]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => selectColor('all')}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') selectColor('all');
                                    }}
                                >
                                    <span className="mobileFilter__items_section_dropdownPanel_itemLabel">Все</span>
                                    {selectedColor === 'all' && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                </div>
                                {colorOptions.map((opt) => (
                                    <div
                                        key={opt}
                                        role="option"
                                        aria-selected={selectedColor === opt}
                                        tabIndex={0}
                                        className={['mobileFilter__items_section_dropdownPanel_dropdownItem', selectedColor === opt ? 'active' : undefined]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => selectColor(opt)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') selectColor(opt);
                                        }}
                                    >
                                        <span className="mobileFilter__items_section_dropdownPanel_itemLabel">{opt}</span>
                                        {selectedColor === opt && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Групповой фильтр из пропсов (совместимость с текущей логикой десктоп) */}
                    {groupFilterLabel && groupFilterOptions && onGroupOptionChange && (
                        <div className="mobileFilter__items_section" ref={groupRef}>
                            <button
                                className="mobileFilter__items_section_dropdownToggle"
                                aria-haspopup="listbox"
                                aria-expanded={isGroupOpen}
                                onClick={() => setIsGroupOpen((v) => !v)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setIsGroupOpen(false);
                                }}
                            >
                                <span className="mobileFilter__items_section_dropdownToggle_label">{groupFilterLabel}:</span>
                                <span className="mobileFilter__items_section_dropdownToggle_selected">
                                    {currentGroupOption === 'all' ? 'Все' : (groupFilterOptions.find((o) => o.value === currentGroupOption)?.label ?? 'Выбрать')}
                                </span>
                                <span
                                    className={`mobileFilter__items_section_dropdownToggle_arrow ${
                                        isGroupOpen ? 'mobileFilter__items_section_dropdownToggle_arrow_open' : ''
                                    }`}
                                    aria-hidden="true"
                                >
                                    ▼
                                </span>
                            </button>
                            <div
                                className={`mobileFilter__items_section_dropdownPanel ${isGroupOpen ? 'mobileFilter__items_section_dropdownPanel_open' : ''}`}
                                role="listbox"
                                aria-label={groupFilterLabel}
                            >
                                <div
                                    role="option"
                                    aria-selected={currentGroupOption === 'all'}
                                    tabIndex={0}
                                    className={['mobileFilter__items_section_dropdownPanel_dropdownItem', currentGroupOption === 'all' ? 'active' : undefined]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => {
                                        onGroupOptionChange('all');
                                        setIsGroupOpen(false);
                                    }}
                                    onKeyDown={(e) => handleGroupKey(e, 'all')}
                                >
                                    <span className="mobileFilter__items_section_dropdownPanel_itemLabel">Все</span>
                                    {currentGroupOption === 'all' && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                </div>
                                {groupFilterOptions.map((opt, index) => (
                                    <div
                                        key={`${opt.value}-${index}`}
                                        role="option"
                                        aria-selected={currentGroupOption === opt.value}
                                        tabIndex={0}
                                        className={[
                                            'mobileFilter__items_section_dropdownPanel_dropdownItem',
                                            currentGroupOption === opt.value ? 'active' : undefined,
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => {
                                            onGroupOptionChange(opt.value);
                                            setIsGroupOpen(false);
                                        }}
                                        onKeyDown={(e) => handleGroupKey(e, opt.value)}
                                    >
                                        <span className="mobileFilter__items_section_dropdownPanel_itemLabel">{opt.label}</span>
                                        {currentGroupOption === opt.value && <span className="mobileFilter__items_section_dropdownPanel_checkmark">✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {hasAnyFilter && (
                    <>
                        <button type="button" className="mobileFilter__box_reset" onClick={resetAllFilters} aria-label="Сбросить все фильтры">
                            Сбросить все
                        </button>
                        <button type="button" className="mobileFilter__box_show" onClick={() => setOpen(false)} aria-label="Показать результат">
                            Показать
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default MobileFilter;
